import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import outputSamplesTableToTable from 'lab-automation/output-samples/outputSamplesTableToTable';
import {RunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import getSampleDetailsFromCell from 'notebook/sample-propagation/sample-collection/getSampleDetailsFromCell';
import getSampleId from 'notebook/sample-propagation/util/getSampleId';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {createRequestActionFunction} from 'notebook/structured-tables/action-functions/createRequestActionFunction';
import {printBarcodesActionFunction} from 'notebook/structured-tables/action-functions/printBarcodesActionFunction';
import {
  ColumnIdentifier,
  getColumnDefinitionIndex,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  ActionFunctions,
  StructuredTable,
  StructuredTableColumnSummary,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {createProcessActionFunction} from 'processes/tables/action-functions/createProcessActionFunction';
import {createRequestV2SubmissionActionFunction} from 'processes/tables/action-functions/createRequestV2SubmissionActionFunction';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {formatGridCoordinates} from 'samples/SampleUtil';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export interface RunOutputSamplesTableMetadata {
  numSamples: number;
  configName: string;
  automationOutputFileId?: string | null; // API identifier of automationOutputFile associated with these run outputs
}

export interface RunOutputSamplesRowMetadata {}

function getSampleIdForRow(row: Row, sampleColumnIndex: number): string | null {
  const entityOrBatchSample = getSampleDetailsFromCell(row[sampleColumnIndex], TableColumnType.SAMPLE);
  return entityOrBatchSample ? getSampleId(entityOrBatchSample[0]) : null;
}

function getContainerIdForRow(row: Row, containersColumnIndex: number): string | null {
  if (containersColumnIndex === -1) {
    return null;
  }
  const containerSample = getSampleDetailsFromCell(row[containersColumnIndex], TableColumnType.CONTAINERS);
  return containerSample ? getSampleId(containerSample[0]) : null;
}

function getSampleInfoColumnIndices(tableColumnDefinitions: Array<ColumnIdentifier | null | undefined>): {
  sampleColumnIndex: number;
  containersColumnIndex: number;
  plateColumnIndex: number;
  positionColumnIndex: number;
} {
  return {
    sampleColumnIndex: getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.SAMPLE,
      tableColumnDefinitions,
      isOptional: true,
    }),
    containersColumnIndex: getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.CONTAINERS,
      tableColumnDefinitions,
      isOptional: true,
    }),
    plateColumnIndex: getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.PLATE,
      tableColumnDefinitions,
      isOptional: true,
    }),
    positionColumnIndex: getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.POSITION,
      tableColumnDefinitions,
      isOptional: true,
    }),
  };
}

function getPlateIdForRow(row: Row, plateColumnIndex: number): string | null {
  if (plateColumnIndex === -1) {
    return null;
  }
  const cell = row[plateColumnIndex];
  if (!cell.typedValue) {
    return null;
  }
  assertAndContinue(
    cell.typedValue.type === BaseTypedValueType.STORABLES,
    'Cells of Plate column should contain plates'
  );
  if (cell.typedValue.type !== BaseTypedValueType.STORABLES) {
    return null;
  }
  return cell.typedValue.storableSummaries[0].id;
}

function getPlateWellPositionStringForRow(row: Row, positionColumnIndex: number): string | null {
  if (positionColumnIndex === -1) {
    return null;
  }
  const cell = row[positionColumnIndex];
  if (!cell.typedValue) {
    return null;
  }
  assertAndContinue(
    cell.typedValue.type === BaseTypedValueType.GRID_COORDINATES,
    'Positions should be GRID_COORDINATES typed values'
  );
  if (cell.typedValue.type !== BaseTypedValueType.GRID_COORDINATES) {
    return null;
  }
  return formatGridCoordinates(
    cell.typedValue.gridCoordinates.rowIndex,
    cell.typedValue.gridCoordinates.columnIndex
  );
}

export default class RunOutputSamplesTableAdapter extends GenericStructuredTableAdapter<
  RunOutputSamplesTableMetadata,
  RunOutputSamplesRowMetadata
> {
  getDisplayName(_: StructuredTable<RunOutputSamplesTableMetadata, {}>): string {
    return 'Output Samples';
  }

  override toTable(outputSamplesTable: RunOutputSamplesTable): Table {
    const {table, widths} = outputSamplesTableToTable(outputSamplesTable);
    return {...table, widths};
  }

  getBlankRowMetadata(tableMetadata: RunOutputSamplesTableMetadata): {} {
    return {};
  }

  override getSampleInfosForRequest(
    structuredTable: RunOutputSamplesTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): Array<SampleLookupInfoForCreateRequest> {
    if (!structuredTable.rows.length) {
      return [];
    }
    const sourceTable = this.toTable(structuredTable);
    const tableColumnDefinitions = sourceTable.columnConstraints.map(
      (constraint) => constraint?.columnDefinition
    );
    const {sampleColumnIndex, containersColumnIndex, plateColumnIndex, positionColumnIndex} =
      getSampleInfoColumnIndices(tableColumnDefinitions);
    const sampleInfos: Array<SampleLookupInfoForCreateRequest> = [];

    if (sampleColumnIndex !== -1) {
      // Rows are samples
      for (let i = 0; i < sourceTable.table.length; i++) {
        const row = sourceTable.table[i];
        const sampleId = getSampleIdForRow(row, sampleColumnIndex);
        const containerId = getContainerIdForRow(row, containersColumnIndex);
        const plateId = getPlateIdForRow(row, plateColumnIndex);
        const position = getPlateWellPositionStringForRow(row, positionColumnIndex);

        if (containerId != null) {
          sampleInfos.push({
            idForLookup: containerId,
          });
        } else if (plateId != null) {
          assertAndContinue(position != null, 'Expected row to have position if it has plate');
          if (position != null) {
            sampleInfos.push({
              idForLookup: plateId,
              plateWellPositionString: position,
            });
          }
        } else if (sampleId != null) {
          sampleInfos.push({
            idForLookup: sampleId,
          });
        }
      }
    } else {
      // Rows are plates
      for (let i = 0; i < sourceTable.table.length; i++) {
        const row = sourceTable.table[i];
        const plateId = getPlateIdForRow(row, plateColumnIndex);
        const position =
          positionColumnIndex !== -1
            ? (getPlateWellPositionStringForRow(row, positionColumnIndex) ?? undefined)
            : undefined;
        assertAndContinue(plateId != null, 'Cells of Plate column should contain plates');
        if (plateId != null) {
          sampleInfos.push({
            idForLookup: plateId,
            plateWellPositionString: position,
          });
        }
      }
    }
    return sampleInfos;
  }

  override getColumnSummariesForCreateProcessShortcut(
    structuredTable: RunOutputSamplesTable
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    const sourceTable = this.toTable(structuredTable);
    const tableColumnDefinitions = sourceTable.columnConstraints.map(
      (constraint) => constraint?.columnDefinition
    );
    // The primary column is either the entity, destination container, or destination plate
    const {sampleColumnIndex, containersColumnIndex, plateColumnIndex, positionColumnIndex} =
      getSampleInfoColumnIndices(tableColumnDefinitions);
    const plateColumnSummary = {
      validDestinationColumnRequirements: [
        {
          columnType: TableColumnType.TAG_VALUE,
          schemaFieldType: FieldType.STORABLE_LINK,
          folderItemType: null,
        },
        {columnType: TableColumnType.PLATE, schemaFieldType: null, folderItemType: null},
      ],
      schemaIdConstraint: null,
      columnIndex: plateColumnIndex,
    };
    if (sampleColumnIndex !== -1) {
      return [
        {
          validDestinationColumnRequirements: [
            {
              columnType: TableColumnType.TAG_VALUE,
              schemaFieldType: FieldType.BIOENTITY_LINK,
              folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
            },
            {
              columnType: TableColumnType.EXISTING_BIOENTITY,
              schemaFieldType: null,
              folderItemType: null,
            },
          ],
          schemaIdConstraint: null,
          columnIndex: sampleColumnIndex,
        },
      ];
    } else if (containersColumnIndex !== -1) {
      return [
        {
          validDestinationColumnRequirements: [
            {
              columnType: TableColumnType.TAG_VALUE,
              schemaFieldType: FieldType.STORABLE_LINK,
              folderItemType: null,
            },
            {
              columnType: TableColumnType.CONTAINERS,
              schemaFieldType: null,
              folderItemType: null,
            },
          ],
          schemaIdConstraint: null,
          columnIndex: containersColumnIndex,
        },
      ];
    } else if (plateColumnIndex !== -1 && positionColumnIndex !== -1) {
      return [{...plateColumnSummary, shouldFetchPlateInfo: true}];
    } else if (plateColumnIndex !== -1) {
      return [plateColumnSummary];
    }
    return [];
  }

  override getActionFunctions(
    tableMetadata: RunOutputSamplesTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<RunOutputSamplesTableMetadata, RunOutputSamplesRowMetadata> {
    return {
      secondary: [
        createRequestActionFunction,
        createProcessActionFunction,
        createRequestV2SubmissionActionFunction,
        printBarcodesActionFunction,
        addToWorklistActionFunction,
      ],
    };
  }
}
